import React from "react";
import { TextField, TextFieldProps } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";

type StyledTextFieldProps = TextFieldProps & {
  /** Horizontal input padding override (vertical centring comes from the fixed height). */
  padding?: string | Record<string, string>;
};

/**
 * Text input for the redesigned quiz, matching the Figma "Open" field exactly:
 * fixed 50px box (60px desktop), 10px padding, white bg, 1px #d7c7b4 border,
 * radius 10 (14 desktop), Archivo 17px/130% (18px desktop) centred text with
 * a #b0b0b0 placeholder.
 */
export const StyledTextField: React.FC<StyledTextFieldProps> = ({
  padding = { xs: "0 10px", md: "0 21px" },
  ...props
}) => (
  <TextField
    {...props}
    variant="outlined"
    sx={{
      width: "100%",
      maxWidth: "500px",
      "& .MuiOutlinedInput-root": {
        height: { xs: "50px", md: "60px" },
        boxSizing: "border-box",
        boxShadow: "none",
        backgroundColor: QUIZ.color.white,
        borderRadius: { xs: QUIZ.radius.input, md: QUIZ.radius.inputLg },
        fontFamily: QUIZ.font.body,
        "& fieldset": { borderColor: QUIZ.color.cream3, borderWidth: "1px" },
        "&:hover fieldset": { borderColor: QUIZ.color.brown },
        "&.Mui-focused fieldset": { borderColor: QUIZ.color.brown, borderWidth: "1px" },

        "& input": {
          padding,
          height: "100%",
          boxSizing: "border-box",
          boxShadow: "none",
          WebkitAppearance: "none",
          fontFamily: QUIZ.font.body,
          fontSize: { xs: "17px", md: "18px" },
          lineHeight: 1.3,
          fontWeight: 400,
          color: QUIZ.color.ink,
          backgroundColor: "transparent",
          textAlign: "center",
          "&::placeholder": { color: QUIZ.color.placeholder, opacity: 1 },
        },

        // Figma inputs have no number spinners (Years / Months / Kg)
        "& input[type=number]": { MozAppearance: "textfield" },
        "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
          { WebkitAppearance: "none", margin: 0 },
      },
      "& .MuiInputLabel-root": { display: "none" },
      ...props.sx,
    }}
  />
);
